import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import useCurrentUser from "../../hooks/useCurrentUser";
import { Search, Loader2 } from "lucide-react";

export default function EvaluationHistory() {
    const { clientId } = useCurrentUser();
    const [evaluations, setEvaluations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchEvaluations = async () => {
        setLoading(true);
        try {
            // Instead of relying on a composite index (orderBy createdAt + where companyId),
            // we fetch by companyId and sort on the client side to avoid index errors initially.
            const q = query(
                collection(db, "landingEvaluations"),
                where("companyId", "==", clientId)
            );
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            data.sort((a, b) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                return timeB - timeA;
            });

            setEvaluations(data);
        } catch (err) {
            console.error("Error fetching evaluations:", err);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (clientId) fetchEvaluations();
    }, [clientId]);

    const filtered = evaluations.filter(ev =>
        ev.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ev.hsCode?.includes(searchTerm)
    );

    const getStatusBadge = (status) => {
        switch (status) {
            case "approved":
                return <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-[11px] font-bold rounded uppercase">Approved</span>;
            case "needs_documents":
                return <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-[11px] font-bold rounded uppercase">Needs Docs</span>;
            case "rejected":
                return <span className="px-2.5 py-1 bg-red-100 text-red-800 text-[11px] font-bold rounded uppercase">Rejected</span>;
            default:
                return <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-[11px] font-bold rounded uppercase">Pending</span>;
        }
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <h2 className="font-bold text-navy-900">Your Evaluations</h2>
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search products or HS..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-gold-500 w-64"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <Loader2 className="animate-spin text-slate-400" size={32} />
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium">
                            <tr>
                                <th className="px-6 py-3">Product</th>
                                <th className="px-6 py-3">HS Code</th>
                                <th className="px-6 py-3">Duty Rate</th>
                                <th className="px-6 py-3">Est. Duty</th>
                                <th className="px-6 py-3">Landed Cost</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-10 text-slate-500">
                                        No evaluations found.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <p className="font-medium text-navy-900">{item.productName}</p>
                                            <p className="text-xs text-slate-400 truncate w-48" title={item.productDescription}>{item.productDescription}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.verifiedHSCode ? (
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-emerald-600">{item.verifiedHSCode}</span>
                                                    <span className="text-[10px] text-slate-400">Verified</span>
                                                </div>
                                            ) : (
                                                <span className="font-mono text-slate-700">{item.hsCode}</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-slate-600">{item.dutyRate}%</td>
                                        <td className="px-6 py-4 font-medium text-navy-900">${(item.customsDuty || 0).toLocaleString()}</td>
                                        <td className="px-6 py-4 font-bold text-navy-900">${(item.landedCost || 0).toLocaleString()}</td>
                                        <td className="px-6 py-4">{getStatusBadge(item.status)}</td>
                                        <td className="px-6 py-4 text-slate-500 text-xs">
                                            {item.createdAt ? new Date(item.createdAt.toDate()).toLocaleDateString() : "Just now"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
